/**
 * What one cohort upload accepted, and who it left out.
 *
 * The counts alone would hide the part that needs acting on, so every exclusion is named
 * with its reason: a patient skipped because a screening was already running is fine and
 * self-resolving, while one the broker refused needs re-uploading.
 */
import { Link } from "react-router-dom";
import type { ScreeningBatchResponse } from "../../api/clinicalTrialsApiTypes";

/**
 * One labelled count in the summary strip.
 *
 * @param label - What is being counted
 * @param value - The count
 * @param muted - Render de-emphasised, for counts that are normal at zero
 * @returns The stat block
 */
function Stat({
  label,
  value,
  muted,
}: {
  label: string;
  value: number;
  muted?: boolean;
}) {
  return (
    <div className="rounded-md border border-gray-200 px-4 py-3">
      <div
        className="text-2xl font-semibold"
        style={{ color: muted ? "var(--color-muted-foreground)" : undefined }}
      >
        {value}
      </div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

/**
 * A named list of MRNs the upload excluded, with the reason each was excluded.
 *
 * @param title - What this group has in common
 * @param note - Why it happened and what to do about it
 * @param entries - MRN paired with its reason, or a bare MRN list
 * @returns The group, or null when nothing was excluded this way
 */
function Excluded({
  title,
  note,
  entries,
}: {
  title: string;
  note: string;
  entries: string[] | Record<string, string>;
}) {
  const rows = Array.isArray(entries)
    ? entries.map((mrn) => [mrn, ""] as const)
    : Object.entries(entries);
  if (!rows.length) return null;

  return (
    <div className="mt-4">
      <h4 className="text-sm font-semibold">
        {title} ({rows.length})
      </h4>
      <p className="text-xs text-gray-500 mb-2">{note}</p>
      <ul className="text-sm space-y-1">
        {rows.map(([mrn, reason]) => (
          <li key={mrn} className="font-mono text-xs">
            {mrn}
            {reason && (
              <span className="font-sans text-gray-500"> — {reason}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Render the outcome of a cohort upload.
 *
 * @param result - What the server reported
 * @returns The result panel
 */
export function CohortResultPanel({
  result,
}: {
  result: ScreeningBatchResponse;
}) {
  return (
    <div className="rounded-lg border border-gray-200 p-6 bg-white">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-lg font-semibold">Cohort queued</h3>
        {result.run_id && (
          <span className="text-xs text-gray-500 font-mono">
            run {result.run_id}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="in the file" value={result.total_in_csv} />
        <Stat label="queued for screening" value={result.queued} />
        <Stat
          label="columns from FHIR"
          value={result.hydrated.length}
          muted={!result.hydrated.length}
        />
        <Stat
          label="left out"
          value={
            result.already_in_flight.length +
            Object.keys(result.publish_failed).length
          }
          muted={
            !result.already_in_flight.length &&
            !Object.keys(result.publish_failed).length
          }
        />
      </div>

      <Excluded
        title="Already being screened"
        note="A screening was already queued or running for these, so this upload left them alone. Nothing to do."
        entries={result.already_in_flight}
      />
      <Excluded
        title="Could not be queued"
        note="The broker refused these. Their rows are marked failed and they can be uploaded again."
        entries={result.publish_failed}
      />
      <Excluded
        title="Screening without display columns"
        note="These are being screened, but FHIR returned nothing to show for them, so their row will be a bare MRN until ingestion fills it in."
        entries={result.hydrate_failed}
      />

      <p className="text-sm text-gray-600 mt-6">
        Verdicts land on the{" "}
        <Link
          to="/eligibility"
          className="font-medium underline"
          style={{ color: "var(--color-primary)" }}
        >
          eligibility worklist
        </Link>{" "}
        as each patient finishes. Nothing is screened yet.
      </p>
    </div>
  );
}
